import React from 'react'
import { css } from '@mxenabled/cssinjs'
import { v4 as uuidv4 } from 'uuid'

import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField'

import { P, TextField } from '@mxenabled/mxui'

import { getLocale } from '../../core'
import { CurrencyCodes, MAX_MONEY_INPUT } from '../../core/constants/Numbers'
import { formatCurrency, getCurrencyCodes } from '../../core/utils/NumberFormatting'
import { CurrencyInputCopy } from '../../core'
import { useGlobalCopyStore } from '../context'
import { buildDynamicCopy } from '../../core/utils/Localization'

type CurrencyInputProps = {
  allowDecimals?: boolean
  amountIsRequired?: boolean
  ariaLabel?: string
  amount: string | number
  copy?: CurrencyInputCopy
  customValidate?: () => boolean
  errorText?: string
  label?: string
  minAmount?: number
  setAmount: (s: string) => void
  setHasValidInput?: (b: boolean) => void
} & MuiTextFieldProps

const CurrencyInput: React.FC<CurrencyInputProps> = ({
  allowDecimals = true,
  ariaLabel,
  amount,
  amountIsRequired = true,
  copy,
  customValidate = () => true,
  errorText = '',
  label,
  minAmount = 1,
  setAmount,
  setHasValidInput,
  ...rest
}) => {
  const [errorMsg, setErrorMsg] = React.useState('')
  const { common: commonCopy } = useGlobalCopyStore()
  const customCopy = copy ? copy : commonCopy.currency_input

  const styles = getStyles()

  const numbersOnly = /^[0-9]*$/
  // disabling this lint rule because this "useless" escape is not useless and breaks functionality if removed
  // eslint-disable-next-line no-useless-escape
  const numbersWithComma = /^(\d)*(\,)?([0-9]{1,2})?$/
  const numbersWithDecimal = /^(\d)*(\.)?([0-9]{1,2})?$/
  let pattern: RegExp

  const nameRef = React.useRef(uuidv4())

  if (allowDecimals) {
    switch (getCurrencyCodes()) {
      case CurrencyCodes.CAD:
        pattern = numbersWithComma
        break
      default:
        pattern = numbersWithDecimal
        break
    }
  } else {
    pattern = numbersOnly
  }

  React.useEffect(() => {
    handleValidation(amount)
  }, [amount])

  const handleValidation = (value: string | number) => {
    if (!amountIsRequired && (value === '' || value === '.' || value === ',')) {
      // don't display error in these instances, but indicate the input isn't valid
      setErrorMsg('')
      setHasValidInput?.(false)
    } else if (Number(value) < minAmount) {
      // display error if number is negative
      setErrorMsg(
        buildDynamicCopy(
          customCopy.error_amount_must_be_equal_to,
          formatCurrency(minAmount, '0'),
        ) as string,
      )
      setHasValidInput?.(false)
    } else if (Number(value) > MAX_MONEY_INPUT) {
      // display error if number is greater than max amount allowed
      setErrorMsg(
        buildDynamicCopy(
          customCopy.error_amount_must_be_less_than_or_equal_to,
          formatCurrency(MAX_MONEY_INPUT, allowDecimals ? '0,0.00' : '0,0', Math.floor),
        ) as string,
      )
      setHasValidInput?.(false)
    } else if (!value.toString().match(pattern)) {
      // check that input is valid and display error on special characters, etc
      setErrorMsg(customCopy.error_amount_is_invalid)
      setHasValidInput?.(false)
    } else if (!customValidate()) {
      // if custom error validation returns false, error passed in by parent is displayed
      setErrorMsg(errorText)
      setHasValidInput?.(false)
    } else {
      // input is valid!
      setErrorMsg('')
      setHasValidInput?.(true)
    }

    setAmount(value.toString())
  }

  return (
    <TextField
      className={`mx-cmn-currency-input ${styles}`}
      error={Boolean(errorMsg)}
      helperText={errorMsg}
      hiddenLabel={!label}
      id={`currency-input-${nameRef.current}`}
      inputMode={allowDecimals ? 'decimal' : 'numeric'}
      label={label}
      name={`currency-input-${nameRef.current}`}
      onChange={(e: any) => handleValidation(e.target.value)}
      slotProps={{
        input: {
          endAdornment: getLocale().includes('fr-ca') ? <P>$</P> : undefined,
          startAdornment: !getLocale().includes('fr-ca') ? <P>$</P> : undefined,
        },
        htmlInput: {
          'aria-label': ariaLabel || customCopy.enter_amount,
          noValidate: true,
          pattern: allowDecimals ? '[0-9]*\\.?[0-9]*' : '[0-9]*',
        },
      }}
      value={amount}
      {...rest}
    />
  )
}

const getStyles = () =>
  css({
    '& .MuiInputBase-adornedStart > .p': {
      marginRight: 4,
    },
  })

export default CurrencyInput
